import type {
  BucketDocument,
  BucketDocumentDraft,
  BucketEntity,
  BucketEntityDraft,
  BucketProfile,
  BucketProfileFact
} from '@oysterjs/smartfill-contracts';
import { humanizeName } from './document';
import { toDraft } from './edits';

/**
 * Facts asked for by at least a quarter of a profile's carriers. With a single
 * carrier that is every fact it asks for.
 */
export function isCommonFact(profile: BucketProfile, fact: BucketProfileFact): boolean {
  return fact.carrierCount >= Math.max(1, Math.ceil(profile.carriers.length / 4));
}

/** One record of a section, or the record the section does not have yet. */
export interface ProfileRecord {
  /** Null when the bucket holds no record at the section's relation path. */
  entity: BucketEntity | null;
  label: string;
  /** A few of the record's own values, such as "2020 Toyota Camry". */
  summary: string;
  held: BucketProfileFact[];
  missing: BucketProfileFact[];
}

/** The facts a profile asks for under one relation path, such as drivers. */
export interface ProfileSection {
  relationPath: string;
  title: string;
  /** "vehicle" for vehicles; how one record of the section is named. */
  recordName: string;
  facts: BucketProfileFact[];
  /** Facts at least one record of the section holds. */
  heldCount: number;
  records: ProfileRecord[];
}

export interface ProfileCoverage {
  heldCount: number;
  factCount: number;
  sections: ProfileSection[];
}

/**
 * How much of a profile a bucket holds. A fact counts once some record at its
 * relation path holds it, since carriers ask about each vehicle or driver the
 * bucket lists rather than a particular one.
 */
export function profileCoverage(
  document: BucketDocument,
  facts: BucketProfileFact[]
): ProfileCoverage {
  const byPath = new Map<string, BucketProfileFact[]>();
  for (const fact of facts) {
    byPath.set(fact.relationPath, [...(byPath.get(fact.relationPath) ?? []), fact]);
  }
  // The client comes first; the other sections follow their most-asked fact.
  const paths = [...byPath.keys()].sort((a, b) => Number(a !== '') - Number(b !== ''));
  const sections = paths.map((relationPath) => {
    const sectionFacts = byPath.get(relationPath) ?? [];
    const entities = recordsAt(document, relationPath);
    const records =
      entities.length > 0
        ? entities.map((entity, index) =>
            profileRecord(
              entity,
              relationPath,
              sectionFacts,
              entities.length > 1 ? index + 1 : null
            )
          )
        : [profileRecord(null, relationPath, sectionFacts, null)];
    return {
      relationPath,
      title: relationPath ? humanizeName(lastRelation(relationPath)) : 'Client',
      recordName: singular(humanizeName(lastRelation(relationPath)).toLowerCase()),
      facts: sectionFacts,
      heldCount: sectionFacts.filter((fact) => entities.some((entity) => holds(entity, fact)))
        .length,
      records
    };
  });
  return {
    heldCount: sections.reduce((sum, section) => sum + section.heldCount, 0),
    factCount: facts.length,
    sections
  };
}

/**
 * The profile whose commonly asked facts the bucket holds the largest share
 * of, or null when no profile matches at least three of them: a bucket that
 * shares only a name and a phone with a profile is not shaped like it.
 */
export function bestProfile(
  document: BucketDocument,
  profiles: BucketProfile[]
): BucketProfile | null {
  let best: BucketProfile | null = null;
  let bestShare = 0;
  for (const profile of profiles) {
    const coverage = profileCoverage(
      document,
      profile.facts.filter((fact) => isCommonFact(profile, fact))
    );
    const share = coverage.factCount ? coverage.heldCount / coverage.factCount : 0;
    if (coverage.heldCount >= 3 && share > bestShare) {
      best = profile;
      bestShare = share;
    }
  }
  return best;
}

/**
 * Sets a profile fact on a record under the profile's own key, so the site
 * profiles recognize it. Without a record it starts one at the fact's
 * relation path, under the first record of each parent relation.
 */
export function setProfileFact(
  document: BucketDocument,
  fact: BucketProfileFact,
  value: string,
  entityId: string | null
): BucketDocumentDraft {
  const draft = toDraft(document);
  const property = {
    semanticKey: fact.semanticKey,
    displayLabel: fact.label,
    kind: fact.kind,
    value
  };
  if (entityId !== null) {
    const entity = findDraftEntity(draft.root, entityId);
    if (entity) {
      const properties = [...(entity.properties ?? [])];
      const index = properties.findIndex((candidate) => candidate.semanticKey === fact.semanticKey);
      if (index >= 0) properties[index] = { ...properties[index], value };
      else properties.push(property);
      entity.properties = properties;
    }
    return draft;
  }
  const relations = fact.relationPath.split('/').filter(Boolean);
  let parent = draft.root;
  relations.forEach((relation, index) => {
    const last = index === relations.length - 1;
    const existing = last
      ? undefined
      : parent.children?.find((child) => child.relation === relation);
    if (existing) {
      parent = existing;
      return;
    }
    const record: BucketEntityDraft = {
      relation,
      type: last ? fact.ownerType : singular(relation)
    };
    parent.children = [...(parent.children ?? []), record];
    parent = record;
  });
  parent.properties = [...(parent.properties ?? []), property];
  return draft;
}

/** The records at a relation path: the root for "", else the children reached through it. */
export function recordsAt(document: BucketDocument, relationPath: string): BucketEntity[] {
  let records = [document.root];
  for (const relation of relationPath.split('/').filter(Boolean)) {
    records = records.flatMap((record) =>
      (record.children ?? []).filter((child) => child.relation === relation)
    );
  }
  return records;
}

function profileRecord(
  entity: BucketEntity | null,
  relationPath: string,
  facts: BucketProfileFact[],
  ordinal: number | null
): ProfileRecord {
  const held = entity ? facts.filter((fact) => holds(entity, fact)) : [];
  const name = relationPath
    ? sentence(singular(humanizeName(lastRelation(relationPath)).toLowerCase()))
    : 'Client';
  return {
    entity,
    label: ordinal === null ? name : `${name} ${ordinal}`,
    summary: held
      .slice(0, 3)
      .map((fact) => valueOf(entity, fact))
      .join(' '),
    held,
    missing: facts.filter((fact) => !held.includes(fact))
  };
}

function holds(entity: BucketEntity, fact: BucketProfileFact): boolean {
  return valueOf(entity, fact) !== '';
}

function valueOf(entity: BucketEntity | null, fact: BucketProfileFact): string {
  return (
    entity?.properties
      ?.find((property) => property.semanticKey === fact.semanticKey)
      ?.value.trim() ?? ''
  );
}

function findDraftEntity(entity: BucketEntityDraft, id: string): BucketEntityDraft | null {
  if (entity.id === id) return entity;
  for (const child of entity.children ?? []) {
    const found = findDraftEntity(child, id);
    if (found) return found;
  }
  return null;
}

function lastRelation(relationPath: string): string {
  return relationPath.split('/').at(-1) ?? relationPath;
}

function singular(words: string): string {
  if (words.endsWith('ies')) return `${words.slice(0, -3)}y`;
  if (words.endsWith('sses')) return words.slice(0, -2);
  return words.endsWith('s') && !words.endsWith('ss') ? words.slice(0, -1) : words;
}

function sentence(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}
